import { Component, OnInit } from '@angular/core';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppointmentService } from 'src/app/services/appointment.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { AnalyticsService } from 'src/app/services/analytics.service';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
})
export class AnalyticsComponent implements OnInit {
  role: string | null = null;
  show = false;
  noAccess = false;
  message = '';

  locations: any[] = [];
  statuses: string[] = ['ALL', 'CREATED', 'REVIEWED', 'ASSESMENT', 'FOLLOWUP', 'COMPLETE', 'CANCEL'];
  scheduleTypes: string[] = ['Appointment', 'First_followup', 'Second_followup'];

  filters: any = {
    locationId: null,
    status: 'ALL',
    scheduleType: 'Appointment',
  };
  fromDateValue: Date | null = null;
  toDateValue: Date | null = null;



  kpis: any = {
    totalAppointments: 0,
    ongoingAppointments: 0,
    completedAppointments: 0,
    cancelledAppointments: 0,
    followupAppointments: 0,
    backgroundPendingCount: 0,
    outstandingCount: 0,
    totalBilled: 0,
    totalReceived: 0,
    totalOutstanding: 0,
    collectionRate: 0,
  };

  statusChartType: ChartType = 'doughnut';
  locationChartType: ChartType = 'bar';
  trendChartType: ChartType = 'line';
  diagChartType: ChartType = 'bar';
  mainChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  statusChartData: ChartData<'doughnut'> = { labels: [], datasets: [{ data: [] }] };
  locationChartData: ChartData<'bar'> = { labels: [], datasets: [{ data: [], label: 'Appointments' }] };
  trendChartData: ChartData<'line'> = { labels: [], datasets: [{ data: [], label: 'Monthly Volume' }] };
  diagnosticianChartData: ChartData<'bar'> = { labels: [], datasets: [{ data: [], label: 'Top Diagnosticians' }] };
  financialChartData: ChartData<'bar' | 'line'> = { labels: [], datasets: [] };

  outstandingPayments: any[] = [];
  backgroundPendingAppointments: any[] = [];

  constructor(
    private analyticsService: AnalyticsService,
    private appointmentService: AppointmentService,
    private authService: AuthenticationService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.role = this.authService.getUserType();
    if (this.role !== 'ADMIN' && this.role !== 'SUPERADMIN') {
      this.noAccess = true;
      this.message = 'Analytics is available only for ADMIN and SUPERADMIN users.';
      return;
    }

    const today = new Date();
    const before = new Date();
    before.setDate(today.getDate() - 90);
    this.toDateValue = today;
    this.fromDateValue = before;

    this.spinner.show();
    this.appointmentService.getLocations().subscribe(
      (res: any) => {
        this.locations = res?.data || [];
        this.loadAnalytics();
      },
      () => {
        this.loadAnalytics();
      }
    );
  }

  applyFilters() {
    this.loadAnalytics();
  }

  resetFilters() {
    const today = new Date();
    const before = new Date();
    before.setDate(today.getDate() - 90);
    this.filters = {
      locationId: null,
      status: 'ALL',
      scheduleType: 'Appointment',
    };
    this.fromDateValue = before;
    this.toDateValue = today;
    this.loadAnalytics();
  }



  private loadAnalytics() {
    this.show = false;
    this.spinner.show();
    this.analyticsService
      .getAdminAnalytics({
        ...this.filters,
        fromDate: this.formatDate(this.fromDateValue),
        toDate: this.formatDate(this.toDateValue),
      })
      .subscribe(
      (res: any) => {
        const payload = res?.data || {};
        this.kpis = payload?.kpis || this.kpis;
        this.outstandingPayments = payload?.outstandingPayments || [];
        this.backgroundPendingAppointments = payload?.backgroundPendingAppointments || [];

        this.statusChartData = this.toDoughnutData(payload?.statusBreakdown, [
          '#355C7D',
          '#2A9D8F',
          '#E9C46A',
          '#F4A261',
          '#E76F51',
          '#6D597A',
          '#4F772D',
        ]);
        this.locationChartData = this.toBarData(payload?.locationBreakdown, 'Appointments by Location', '#1F7A8C');
        this.trendChartData = this.toTrendData(payload?.monthlyTrend);
        this.diagnosticianChartData = this.toTopDiagData(payload?.topDiagnosticians);
        this.financialChartData = this.toFinancialTrendData(payload?.financialTrend);

        this.show = true;
        this.spinner.hide();
      },
      (err) => {
        this.message = err?.error?.message || 'Unable to load analytics right now.';
        this.spinner.hide();
      }
    );
  }

  private toDoughnutData(source: any, colors: string[]): ChartData<'doughnut'> {
    const labels = Object.keys(source || {});
    const values = labels.map((k) => source[k]);
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
        },
      ],
    };
  }

  private toBarData(source: any, label: string, color: string): ChartData<'bar'> {
    const labels = Object.keys(source || {});
    const values = labels.map((k) => source[k]);
    return {
      labels,
      datasets: [
        {
          data: values,
          label,
          backgroundColor: color,
          borderRadius: 6,
        },
      ],
    };
  }

  private toTrendData(source: any[]): ChartData<'line'> {
    const labels = (source || []).map((s) => s.month);
    const values = (source || []).map((s) => s.count);
    return {
      labels,
      datasets: [
        {
          data: values,
          label: 'Monthly Volume',
          borderColor: '#0B6E4F',
          backgroundColor: 'rgba(11, 110, 79, 0.2)',
          fill: true,
          tension: 0.25,
        },
      ],
    };
  }

  private toTopDiagData(source: any[]): ChartData<'bar'> {
    const labels = (source || []).map((s) => s.name);
    const values = (source || []).map((s) => s.count);
    return {
      labels,
      datasets: [
        {
          data: values,
          label: 'Top Diagnosticians',
          backgroundColor: '#9C6644',
          borderRadius: 6,
        },
      ],
    };
  }

  private toFinancialTrendData(source: any[]): ChartData<'bar' | 'line'> {
    const labels = (source || []).map((s) => s.month);
    const billed = (source || []).map((s) => s.billed);
    const received = (source || []).map((s) => s.received);
    const outstanding = (source || []).map((s) => s.outstanding);
    
    return {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Billed ($)',
          data: billed,
          backgroundColor: '#2A9D8F',
          borderRadius: 4,
          order: 2
        },
        {
          type: 'bar',
          label: 'Received ($)',
          data: received,
          backgroundColor: '#E9C46A',
          borderRadius: 4,
          order: 3
        },
        {
          type: 'line',
          label: 'Outstanding ($)',
          data: outstanding,
          borderColor: '#E76F51',
          backgroundColor: '#E76F51',
          fill: false,
          tension: 0.25,
          order: 1
        }
      ],
    };
  }

  private formatDate(date: Date | null): string {
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
